import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { requireLegacy } from '../../legacy/legacyGlobals';
import { unmountExtractedScreen } from '../../legacy/screenHost';
import { readStoredJson } from '../../shared/storage/localStore';
import { STORAGE_KEYS } from '../../shared/storage/storageKeys';
import type { JourneyFlow } from '../../shared/state/journeyFlow';
import type { ReadingPreferences } from '../../shared/state/readingPreferences';
import { loadReadingStreak } from '../../shared/state/readingStreak';
import { LanguageSheet } from '../language/LanguageSheet';
import { flagSvg, languageForCode } from '../language/languages';
import { loadCampaign, PAGES_PER_KHATAM, remainingTimeText } from './campaign';
import { HowItWorksSheet } from './HowItWorksSheet';
import { IC_KHATAM, IC_PAGES, IC_READERS, KBIRD, KGLOBE } from './landingArt';
import { useLiveCounters } from './useLiveCounters';

const asHtml = (html: string) => ({ __html: html });

const FLAME_IC = '<svg class="v2flame" width="15" height="17" viewBox="0 0 24 28" fill="none" aria-hidden="true">'
  + '<path d="M13.2 1.5c.6 4-1.5 5.6-3.4 7.5C7.6 11.2 5 13.6 5 17.6 5 22.2 8.7 26 13 26s8-3.6 8-8.4c0-3.7-2-6-3.9-8.2-1.2 1-1.6 2.2-1.6 3.6-1.6-2-2.6-4.4-2.3-11.5z" fill="url(#v2flameg)"/>'
  + '<path d="M13 26c-2.3 0-4.1-1.9-4.1-4.3 0-2.2 1.4-3.4 2.5-4.6.9-1 1.4-2 1.3-3.4 1.6 1.2 2.6 2.4 3.4 3.6.8 1.3 1.1 2.6 1.1 4.4 0 2.4-1.9 4.3-4.2 4.3z" fill="#FFE6B0" fill-opacity=".85"/>'
  + '<defs><linearGradient id="v2flameg" x1="0" y1="0" x2="0" y2="1">'
  + '<stop offset="0" stop-color="#FFD27A"/><stop offset=".55" stop-color="#F79B34"/><stop offset="1" stop-color="#E4622B"/>'
  + '</linearGradient></defs></svg>';

const HOURGLASS_IC = '<svg width="12" height="14" viewBox="0 0 20 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
  + '<path d="M4.5 2h11M4.5 22h11"/>'
  + '<path d="M6 2v3.1c0 2.2 1.7 3.5 3.2 4.5l.8.5-.8.5C7.7 11.6 6 12.9 6 15.1V22"/>'
  + '<path d="M14 2v3.1c0 2.2-1.7 3.5-3.2 4.5l-.8.5.8.5c1.5 1 3.2 2.3 3.2 4.5V22"/></svg>';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/* the sky artwork is composed for this height; a shorter sky scales it down as one piece */
const SKY_DESIGN_HEIGHT = 330;
/* nothing left to count down: the campaign has ended (or has no usable date) */
const liveTimeLeft = (deadline: string | null) => {
  if (!deadline || new Date(deadline).getTime() <= Date.now()) return null;
  return remainingTimeText(deadline);
};
const n = (v: number) => v.toLocaleString('en-US');

/**
 * SCREEN 0 — the v2 Live Global Khatam landing (owner-approved redesign,
 * 2026-08-22, from the "Global Khatam v2" design canvas): the sky animation
 * with a reading-streak chip, a "Join our Global Khatam for:" headline
 * leading into the campaign box (cause hero, countdown, khatam thermometer
 * with a fixed target), and the compact stat row. Campaign figures come from
 * window.CAMPAIGN (env.js placeholders now, the admin backend later).
 */
export function LandingScreen() {
  const t = requireLegacy('t');
  /* Read once per paint, exactly like the legacy screen: the legacy code
     paths that change these persist first and then repaint the landing. */
  const [langCode] = useState(() => readStoredJson<Partial<ReadingPreferences>>(STORAGE_KEYS.readingPreferences, {}).lang || 'en');
  const [account] = useState(() => readStoredJson<Partial<JourneyFlow>>(STORAGE_KEYS.journeyFlow, {}).account ?? null);
  const [streak] = useState(() => loadReadingStreak());
  const [campaign] = useState(() => loadCampaign());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [worksOpen, setWorksOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() => liveTimeLeft(campaign.deadline));
  const skyRef = useRef<HTMLDivElement>(null);
  const { readingRef, pagesRef, stopDrift } = useLiveCounters();

  /* the countdown ticks by the minute — cheap, and always honest on screen */
  useEffect(() => {
    if (!campaign.deadline) return;
    const tick = window.setInterval(() => setTimeLeft(liveTimeLeft(campaign.deadline)), 60000);
    return () => window.clearInterval(tick);
  }, [campaign.deadline]);

  /* On short phones the sky gets less height; scale the whole picture (Allah, logo, globe,
     birds) to fit it instead of letting the globe ride up over the logo and off the top. */
  useLayoutEffect(() => {
    const sky = skyRef.current;
    if (!sky) return;
    const fit = () => sky.style.setProperty('--sky-s', String(Math.min(1, sky.clientHeight / SKY_DESIGN_HEIGHT)));
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(fit);
    watch.observe(sky);
    return () => watch.disconnect();
  }, []);

  const currentLanguage = languageForCode(langCode);
  const fillPct = Math.min(100, Math.round((campaign.khatamsDone / campaign.khatamTarget) * 100));
  const pagesRead = campaign.khatamsDone * PAGES_PER_KHATAM;

  const openLanguageSheet = () => {
    stopDrift();
    setSheetOpen(true);
  };

  const joinKhatam = () => {
    unmountExtractedScreen();
    requireLegacy('route')({ from: 'landing' });
  };

  const startAsNewReader = () => {
    unmountExtractedScreen();
    requireLegacy('startGuidedTour')();
  };

  const openLogin = () => {
    requireLegacy('screenLogin')();
  };

  const logout = () => {
    requireLegacy('doLogout')();
  };

  return (
    <div className="lscreen">
      <button
        className="langchip"
        title={t('langPick')}
        aria-label={t('langPick')}
        onClick={openLanguageSheet}
        dangerouslySetInnerHTML={asHtml(flagSvg(currentLanguage.cc) + '<span>' + currentLanguage.code.toUpperCase() + '</span>')}
      />
      <div className="lsky" ref={skyRef}>
        <div className="lskyart">
        <div className="kallah">ٱللَّهُ</div>
        <div className="kglobe" dangerouslySetInnerHTML={asHtml(KGLOBE)} />
        <div className="klogo" dangerouslySetInnerHTML={asHtml(window.KLOGO || '')} />
        {['b1', 'b2', 'b3'].map((c) => (
          <span key={c} className={'kbird ' + c} dangerouslySetInnerHTML={asHtml(KBIRD)} />
        ))}
        <span className="kbeam" />
        <span className="kriser" dangerouslySetInnerHTML={asHtml(window.KQURANVEC || '')} />
        <span className="kportal" />
        {Array.from({ length: 8 }, (_, i) => (
          <span key={i} className="kspark" style={{ transform: 'rotate(' + i * 45 + 'deg)' }}>
            <i />
          </span>
        ))}
        </div>
        <span className="v2skychips">
          {streak >= 1 && (
            <span className="v2streak">
              <span dangerouslySetInnerHTML={asHtml(FLAME_IC)} />
              <b>{streak}</b>
              <small>{t('streakDaysL')}</small>
            </span>
          )}
          {/* with a streak beside it there is no room for the words: the same button, as a ? */}
          {streak >= 1 ? (
            <button className="v2howworks v2howicon" onClick={() => setWorksOpen(true)} aria-label={t('howWorksT')} title={t('howWorksT')}>
              <span aria-hidden="true">?</span>
            </button>
          ) : (
            <button className="v2howworks" onClick={() => setWorksOpen(true)}>
              <span dangerouslySetInnerHTML={asHtml('<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 10.6V16.4"/><path d="M12 7.6v.2"/></svg>')} />
              {t('howWorksT')}
            </button>
          )}
        </span>
      </div>
      <div className="v2cta">
        <h2 className="v2headline">{t('v2join')}</h2>
      </div>
      <div className="v2camp">
        <div className="v2hero"><img src="img/gaza-hero.jpg" alt={t('causeName')} /></div>
        <div className="v2body">
          <div className="v2head">
            <h3 className="v2title">{t('causeName')}</h3>
          </div>
          <div className="v2meter">
            <div className="v2track">
              <span className="v2done" style={{ left: clamp(fillPct, 9, 91) + '%' }}>{n(campaign.khatamsDone)}</span>
              <div className="v2bar"><i style={{ width: fillPct + '%' }} /></div>
              <span className="v2pagemark" style={{ left: clamp(fillPct, 21, 79) + '%' }}>
                {n(pagesRead) + ' ' + t('pagesAllTime')}
              </span>
            </div>
            <div className="v2target">
              <b>{n(campaign.khatamTarget)}</b>
              <span className="v2tlabel">
                <span dangerouslySetInnerHTML={asHtml(IC_KHATAM)} />
                <small>{t('statKhatams')}</small>
              </span>
            </div>
          </div>
        </div>
      </div>
      {timeLeft && (
        <div className="v2timerrow">
          <span className="v2timer">
            <span dangerouslySetInnerHTML={asHtml(HOURGLASS_IC)} />
            {t('campLeft', { t: timeLeft })}
          </span>
        </div>
      )}
      <div className="lstats v2stats">
        <div className="lstat">
          <span className="v2statlabel">
            <span dangerouslySetInnerHTML={asHtml(IC_READERS)} />
            <small>{t('statReading')}</small>
          </span>
          <b ref={readingRef}>0</b>
        </div>
        <div className="lstat">
          <span className="v2statlabel">
            <span dangerouslySetInnerHTML={asHtml(IC_KHATAM)} />
            <small>{t('statKhatams')}</small>
          </span>
          <b>{n(campaign.khatamsDone)}</b>
        </div>
        <div className="lstat">
          <span className="v2statlabel">
            <span dangerouslySetInnerHTML={asHtml(IC_PAGES)} />
            <small>{t('statPages')}</small>
          </span>
          <b ref={pagesRef}>0</b>
        </div>
      </div>
      <div className="lfoot">
        <div className="llive" dangerouslySetInnerHTML={asHtml('<i></i> ' + t('liveNow'))} />
        {account ? (
          <>
            <button className="lbtn primary" onClick={joinKhatam}>{t('btnJoin')}</button>
            <div className="lsignedin">
              {t('signedAs') + ' ' + account + ' · '}
              <button className="llogout" onClick={logout}>{t('logout')}</button>
            </div>
          </>
        ) : (
          <>
            <button className="lbtn primary" onClick={startAsNewReader}>{t('btnNew')}</button>
            <button className="lbtn ghost" onClick={openLogin}>{t('btnLogin')}</button>
          </>
        )}
      </div>
      {worksOpen && (
        <HowItWorksSheet
          onClose={() => setWorksOpen(false)}
          onJoin={() => {
            setWorksOpen(false);
            if (account) joinKhatam();
            else startAsNewReader();
          }}
        />
      )}
      {sheetOpen && (
        <LanguageSheet
          currentCode={langCode}
          onClose={() => setSheetOpen(false)}
          onLanguageChanged={() => requireLegacy('screenLanding')()}
        />
      )}
    </div>
  );
}
